import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './JitsiMeeting.css';

/**
 * JitsiMeeting Component
 *
 * Embeds Jitsi Meet with custom features:
 * - Virtual backgrounds (built-in Jitsi feature)
 * - Automatic recording via Jigasi (auto-invites transcriber on meeting join)
 *
 * Props:
 * - meetingId: Meeting identifier
 * - roomName: Jitsi room name
 * - jwtToken: JWT token for Jitsi authentication
 * - displayName: (optional) Display name for guest users
 * - onLeave: Callback when user leaves meeting
 */
const JitsiMeeting = ({ meetingId, roomName, jwtToken, displayName, onLeave }) => {
  const { user } = useAuth();
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const [processingEnabled, setProcessingEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!jitsiContainerRef.current) return;

    // JWT authentication is required
    if (!jwtToken) {
      console.error('JWT token is required for Jitsi authentication');
      setError('Authentication token missing. Please try refreshing the page.');
      setIsLoading(false);
      return;
    }

    // Load Jitsi Meet API script
    const loadJitsiScript = () => {
      return new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) {
          console.log('JitsiMeetExternalAPI already loaded');
          resolve();
          return;
        }

        const script = document.createElement('script');
        // Use our self-hosted Jitsi server
        // Development: https://localhost:38443 (Lima VM with HTTPS)
        // Production: https://meet.tetasdeespanol.com
        const jitsiDomain = process.env.REACT_APP_JITSI_DOMAIN || 'localhost:38443';
        const isLocalDev = jitsiDomain.includes('localhost');
        // Use HTTP for local dev, HTTPS for production
        // Always use HTTPS for Jitsi (local dev now uses https://localhost:38443)
        const protocol = 'https';
        const scriptSrc = `${protocol}://${jitsiDomain}/external_api.js`;
        console.log('Loading Jitsi script from:', scriptSrc);
        script.src = scriptSrc;
        script.async = true;
        script.onload = () => {
          console.log('Jitsi script loaded successfully');
          resolve();
        };
        script.onerror = (err) => {
          console.error('Failed to load Jitsi script:', err);
          reject(new Error('Failed to load Jitsi script from ' + scriptSrc));
        };
        document.body.appendChild(script);
      });
    };

    const initializeJitsi = async () => {
      try {
        console.log('Initializing Jitsi with meetingId:', meetingId, 'roomName:', roomName);

        // Use our self-hosted Jitsi server (never use public meet.jit.si)
        // Development: localhost:38443 (Lima VM with HTTPS)
        // Production: meet.tetasdeespanol.com
        const jitsiDomain = process.env.REACT_APP_JITSI_DOMAIN || 'localhost:38443';
        const isLocalDev = jitsiDomain.includes('localhost');

        // Load Jitsi Meet External API script (works for both local HTTPS and production)
        await loadJitsiScript();

        // Use HTTP for local dev, HTTPS for production
        // Always use HTTPS for Jitsi (local dev now uses https://localhost:38443)
        const protocol = 'https';
        const domain = jitsiDomain;
        const roomUrl = `${protocol}://${jitsiDomain}/${roomName || meetingId}`;

        console.log('Jitsi domain:', domain);
        console.log('Jitsi room URL:', roomUrl);
        console.log('Room name:', roomName || meetingId);

        const options = {
          roomName: roomName || meetingId,
          width: '100%',
          height: '100%',
          parentNode: jitsiContainerRef.current,
          // JWT token for authentication
          jwt: jwtToken,
          userInfo: {
            displayName: displayName || user?.name || 'Guest',
            email: user?.email,
          },
          configOverwrite: {
            // DON'T override hosts/bosh/websocket for local dev
            // Let Jitsi use its default config (meet.jitsi domain)
            // This is critical - Prosody is configured for meet.jitsi, not 127.0.0.1!

            // Enable virtual backgrounds
            disableVirtualBackground: false,

            // Start with audio and video muted
            startWithAudioMuted: false,
            startWithVideoMuted: false,

            // UI customizations
            prejoinPageEnabled: false,
            disableDeepLinking: true,

            // NOTE: enableClosePage causes Jitsi to redirect to its own close page
            // We don't need it - videoConferenceLeft event fires without it
            // enableClosePage: false,

            // Virtual background settings
            virtualBackgrounds: {
              enabled: true,
              backgroundType: 'image',
              images: ['background1.jpg', 'background2.jpg', 'background3.jpg'],
              blurValue: 10,
            },

            // Toolbar customization
            toolbarButtons: [
              'camera',
              'chat',
              'closedcaptions', // Transcription button
              'desktop',
              'microphone',
              'hangup',
              'raisehand',
              'tileview',
              'select-background', // Virtual background button
              'settings',
              '__end',
            ],
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            TOOLBAR_ALWAYS_VISIBLE: false, // Toolbar autohides
            MOBILE_APP_PROMO: false,
          },
        };

        console.log('Creating JitsiMeetExternalAPI with options:', { domain, roomName: options.roomName });

        // Use Jitsi External API for both local dev and production
        // The External API properly passes JWT tokens to Prosody
        const api = new window.JitsiMeetExternalAPI(domain, options);
        console.log('JitsiMeetExternalAPI created successfully');
        jitsiApiRef.current = api;

        // Track if we've hidden the loading overlay (prevent multiple triggers)
        let loadingHidden = false;
        const hideLoading = (source) => {
          if (!loadingHidden) {
            console.log(`✅ Hiding loading overlay (triggered by: ${source})`);
            loadingHidden = true;
            setIsLoading(false);
          }
        };

        // Set a fallback timeout to hide loading overlay if events don't fire
        // This handles cases where Jitsi joins successfully but events don't fire properly
        const timeoutId = setTimeout(() => {
          hideLoading('timeout-fallback');
        }, 5000); // 5 seconds

        // Listen for multiple conference events (different Jitsi versions fire different events)
        api.addEventListener('videoConferenceJoined', () => {
          console.log('📹 videoConferenceJoined event fired');
          clearTimeout(timeoutId);
          hideLoading('videoConferenceJoined');
        });

        api.addEventListener('participantJoined', (participant) => {
          console.log('👤 participantJoined event fired:', participant.displayName);
          // Hide loading when the local participant joins
          if (participant.isLocal) {
            clearTimeout(timeoutId);
            hideLoading('participantJoined-local');
          }
        });

        // Also listen for when the API is ready
        api.on('readyToClose', () => {
          console.log('🚪 Conference ready to close - calling onLeave');
          if (onLeave) onLeave();
        });

        // Listen for when video conference is left
        api.addEventListener('videoConferenceLeft', () => {
          console.log('🚪 User left conference - calling onLeave');
          if (onLeave) onLeave();
        });

      } catch (err) {
        console.error('Failed to initialize Jitsi:', err);
        setError('Failed to load video conference. Please try again.');
        setIsLoading(false);
      }
    };

    initializeJitsi();

    // Cleanup
    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
    };
  }, [meetingId, roomName, jwtToken, user, onLeave]);

  if (error) {
    return (
      <div className="jitsi-error">
        <h2>Unable to Load Meeting</h2>
        <p>{error}</p>
        <button onClick={onLeave} className="btn-back">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="jitsi-meeting-container">
      {isLoading && (
        <div className="jitsi-loading">
          <div className="spinner"></div>
          <p>Loading video conference...</p>
        </div>
      )}

      <div ref={jitsiContainerRef} className="jitsi-iframe" />
    </div>
  );
};

export default JitsiMeeting;
